import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocket } from '../../context/SocketContext';

const OPEN_CHAT_SHEET_EVENT = 'bandmate:open-chat-sheet';

export function openChatSheet() {
  window.dispatchEvent(new CustomEvent(OPEN_CHAT_SHEET_EVENT));
}

export function ChatMobile() {
  const { bandmates, totalUnreadChat, onlineUsers, openDM, activeConversationId, groupConversations, openConversation } = useSocket();
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetOpenRef = useRef(false);

  // Listen for external open requests (e.g. from the online users pill in Layout).
  // Must be unconditional — hooks cannot be called after an early return.
  useEffect(() => {
    const handler = () => {
      if (!activeConversationId) setSheetOpen(true);
    };
    window.addEventListener(OPEN_CHAT_SHEET_EVENT, handler);
    return () => window.removeEventListener(OPEN_CHAT_SHEET_EVENT, handler);
  }, [activeConversationId]);

  // Don't render if no bandmates or chat drawer is already open
  if (!bandmates || bandmates.projects.length === 0) return null;
  if (activeConversationId) return null;

  const renderAvatar = (name: string, avatarUrl?: string | null, isOnline?: boolean) => {
    const initials = name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    return (
      <div className="relative flex-shrink-0">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name}
            referrerPolicy="no-referrer"
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-semibold">
            {initials}
          </div>
        )}
        {isOnline !== undefined && (
          <span
            className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-surface ${
              isOnline ? 'bg-green-500' : 'bg-zinc-400'
            }`}
          />
        )}
      </div>
    );
  };

  const seenIds = new Set<string>();
  const allMembers: {
    id: string;
    name: string;
    avatarUrl?: string | null;
    isOnline: boolean;
    unreadCount: number;
    projectNames: string[];
  }[] = [];

  for (const project of bandmates.projects) {
    for (const member of project.members) {
      if (seenIds.has(member.id)) {
        const existing = allMembers.find((m) => m.id === member.id);
        if (existing) existing.projectNames.push(project.name);
        continue;
      }
      seenIds.add(member.id);
      allMembers.push({
        id: member.id,
        name: member.name,
        avatarUrl: member.avatarUrl,
        isOnline: onlineUsers.has(member.id) || member.isOnline,
        unreadCount: member.unreadCount,
        projectNames: [project.name],
      });
    }
  }

  allMembers.sort((a, b) => {
    if (b.unreadCount !== a.unreadCount) return b.unreadCount - a.unreadCount;
    if (b.isOnline !== a.isOnline) return b.isOnline ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

  const handleOpen = (memberId: string) => {
    setSheetOpen(false);
    sheetOpenRef.current = false;
    openDM(memberId);
  };

  const handleOpenGroup = (conversationId: string) => {
    setSheetOpen(false);
    sheetOpenRef.current = false;
    openConversation(conversationId);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    sheetOpenRef.current = false;
  };

  return (
    <>
      {/* FAB — hidden on desktop */}
      <button
        onClick={() => setSheetOpen(true)}
        className="lg:hidden fixed bottom-6 right-4 z-30 w-14 h-14 rounded-full bg-primary shadow-lg flex items-center justify-center text-white hover:bg-primary/90 active:scale-95 transition-transform"
        aria-label="Open chat"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        {totalUnreadChat > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none">
            {totalUnreadChat > 99 ? '99+' : totalUnreadChat}
          </span>
        )}
      </button>

      {/* Bottom sheet */}
      <AnimatePresence>
        {sheetOpen && (
          <>
            <motion.div
              key="chat-sheet-backdrop"
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={closeSheet}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              aria-hidden="true"
            />

            <motion.div
              key="chat-sheet"
              className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-surface rounded-t-2xl shadow-2xl flex flex-col max-h-[75vh]"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'tween', duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            >
            <div className="flex flex-col items-center pt-3 pb-2 px-4 border-b border-border flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-border mb-3" />
              <div className="w-full flex items-center justify-between">
                <h2 className="font-semibold text-text">BandMates</h2>
                <button
                  onClick={closeSheet}
                  className="p-1 text-muted hover:text-text transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-4 py-3 space-y-1">
              {allMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => handleOpen(member.id)}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface-light active:bg-surface-light transition-colors text-left"
                >
                  {renderAvatar(member.name, member.avatarUrl, member.isOnline)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text truncate">{member.name}</p>
                    <p className="text-xs text-muted truncate">
                      {member.isOnline ? (
                        <span className="text-green-500">Online</span>
                      ) : (
                        'Offline'
                      )}
                      {member.projectNames.length > 0 && (
                        <span> · {member.projectNames.slice(0, 2).join(', ')}{member.projectNames.length > 2 ? ` +${member.projectNames.length - 2}` : ''}</span>
                      )}
                    </p>
                  </div>
                  {member.unreadCount > 0 && (
                    <span className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center leading-none flex-shrink-0">
                      {member.unreadCount > 99 ? '99+' : member.unreadCount}
                    </span>
                  )}
                </button>
              ))}

              {groupConversations.length > 0 && (
                <>
                  <div className="pt-3 pb-1 px-3">
                    <p className="text-xs font-semibold text-muted uppercase tracking-wider">Groups</p>
                  </div>
                  {groupConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => handleOpenGroup(conv.id)}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface-light active:bg-surface-light transition-colors text-left"
                    >
                      <div className="relative flex-shrink-0 w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-text truncate">{conv.name || 'Group Chat'}</p>
                        <p className="text-xs text-muted truncate">
                          {conv.members.length} members
                          {conv.project && <span> · {conv.project.name}</span>}
                        </p>
                      </div>
                      {conv.unreadCount > 0 && (
                        <span className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center leading-none flex-shrink-0">
                          {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                        </span>
                      )}
                    </button>
                  ))}
                </>
              )}
            </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
